import { ISO8601 } from '../support/ISO8601';
import { IKeyWithMembers } from '../caremap/ICareMap';
import { State } from '@meraki-internal/state';
import { replaceItem } from '../utils/arrays';
import { BufferedEventBus } from '../utils/BufferedEventBus';
import { RECORDING_SERVICE_STATUS_EVENT, RECORDING_ERROR_EVENT, RecordingService } from './RecordingService';
import { getSuccessMessage, getFakeRecordingStats, IRecordingStats } from '@innerhive-internal/innerhive-common';
import * as RecordingMessages from './RecordingMessages';
import { RecordingStatsClient } from './RecordingStatsClient';
import { CaremapNotesModelFactory } from '../notes/model/CaremapNotesModelFactory';
import { ICareMapNoteTarget } from '../notes/model/ICareMapNoteTarget';
import { Logger } from '../support/Logger';
import { Stopwatch } from '../utils/Stopwatch';
import { getMissingAudioMs } from './MissingAudio';
import { CareMapState } from '../caremap/CareMapState';
import { IFreeTierLimits } from '../innerhive-plus/revenue-cat/IFreeTierLimits';
import { APP_FOREGROUNDED } from '../AppContainer';
import { VoiceRecorder } from '@independo/capacitor-voice-recorder';
import { AlertPresenter } from '../AlertBinder';
import { AppPrincipal } from '../auth/AppPrincipal';
import { isPlatform } from '../utils/isPlatform';
import { isAutomatedTests } from '../utils/isAutomatedTests';
import dayjs from 'dayjs';
import { NetworkAdapter } from '../support/NetworkAdapter';
import { OfflineRecordingQueue } from './OfflineRecordingQueue';
import { MixPanelEventEmitter } from '../metrics/MixPanelEventEmitter';
import { ICaremapNoteResponse, NotesClient } from '../notes/model/NotesClient';
import { AsyncStorageProvider } from '../support/AsyncStorageProvider';
import { UserState } from '../user/UserState';
import { classifyRecording } from '@innerhive-internal/innerhive-common';
import { DevSettings } from '../support/DevSettings';
import { ITriggerPaywallSource } from '../innerhive-plus/InnerhivePlusDialogViewModel';

export type IRecordingStatus = 'recording-complete' | 'uploading' | 'uploaded' | 'upload-failed' | 'transcribed' | 'transcribe-failed' | 'summarized' | 'summarize-failed' | 'offline-pending';

export interface IRecordingSummary {
    title: string;
    participants?: string[];
    shortSummary: string; // listing
    highLevelSummary: string; // overview of what AI generates
    detailedSummary?: { // bulk of what AI generates
        keyTopicsDiscussed?: string[];
        decisionsMade?: string[];
        instructionsOrRecommendations?: string[];
    };
    actionItems?: {
        id: string;
        description: string;
        details?: string;
        completed?: boolean;
        completedAt?: ISO8601;
        completedBy?: string;
        category?: 'take-action-today' | 'schedule-these' | 'ongoing-care';
        when?: string;
        assignedTo?: string;
    }[];
    isUnavailable?: boolean;
    beaComment?: string;
}

/** One extracted care-network member. `id` is the care-map member the entry refers to; optional because entries persisted before ids were included have none. */
export interface IExtractedSupportEntry {
    id?: string;
    name?: string;
    role?: string;
    category: IKeyWithMembers;
}

export interface IExtractedFields {
    careRecipientName?: string;
    relationship?: string;
    diagnoses?: string[];
    allergies?: string[];
    medications?: string[];
    address?: string;
    additionalCareRecipientsMentioned?: string[];
    dob?: string;
    contactInfo?: string[];
    supportsAdded?: IExtractedSupportEntry[];
    supportsUpdated?: IExtractedSupportEntry[];
}

/** Mirrors API response shape — present on IRecording.extractedMemoryItems when returned from the server */
export interface IExtractedMemoryItem {
    fact: string;
    category: string;
}

export type ISummaryPrompt = 'intro' | 'appointment';

export interface IRecordingSavedModalContent {
    title: string;
    message: string;
    isIntro: boolean;
    showProcessingIndicator: boolean;
    buttonText: string;
}

export interface IRecording {
    id: string;
    fileName: string;

    file: ArrayBuffer;
    url: string;

    // TODO remove noteId from this interface
    // It is specific to recordings that are in IndexedDB, and will be missing when part of a saved note
    noteId: string;

    status: IRecordingStatus;

    appointmentId?: string;

    // Care map this recording belongs to
    // Stored in IndexedDB but stripped when attached to note (note becomes source of truth)
    careMapUserId: string;
    careMapId: string;

    // The user who created this recording (principal.userId at time of recording)
    userId: string;

    summary?: IRecordingSummary;
    transcription?: string;
    skipAIProcessing?: boolean;
    summaryPrompt?: ISummaryPrompt;
    extractedFields?: IExtractedFields;
    extractedMemoryItems?: IExtractedMemoryItem[];

    mimeType: string;
    msDuration: number;
    byteLength: number;

    summaryRating?: 'positive' | 'negative';

    created: ISO8601;
    createdAtOverride?: ISO8601; // Optional override for dev testing

    // Which note this recording creates. Defaults to the care recipient ({ section: 'patient' })
    // when unset. Persisted so the offline/retry path re-creates the note on the same target.
    noteTarget?: ICareMapNoteTarget;

    // True when this recording came from an uploaded file rather than the live mic. Persisted on
    // the note's recording so analytics can derive the note's creation method without an event join.
    isUpload?: boolean;
};

export type IRecordingData = Pick<IRecording, 'mimeType' | 'msDuration' | 'file'> & {
    createdAtOverride?: string;
    noteTarget?: ICareMapNoteTarget;
    isUpload?: boolean;
};

type ICurrentRecordingExtras = {
    appointmentId?: string;
    initiatedFrom?: string;
    recordingContext?: 'at-appointment' | 'post-appointment';
    summaryPrompt?: ISummaryPrompt;
    noteTarget?: ICareMapNoteTarget;
};

type IRecordingState = {
    isRecording: boolean;

    // True from the moment a start is committed to until the mic is actually capturing.
    // Acquiring the mic takes ~1.5s on web, and nothing is recorded during it, so the
    // recording UI stays hidden and the triggering control shows a busy state instead.
    isStartingRecording: boolean;

    isPaused: boolean;

    isSheetMinimized: boolean;

    recordings: IRecording[];
    offlineRecordings: IRecording[];
    recordingInterrupted?: boolean;
    isInterrupted?: boolean;
    needsManualResume?: boolean;
    missingAudioMs?: number;
    showRecordingInterruptedAlert? : boolean;

    // so things like buttons in Alerts can use the Recorder
    forceStartRecording?: boolean;
    currentRecordingExtras?: ICurrentRecordingExtras;

    // Network and offline state
    isOnline: boolean;
    pendingUploadCount: number;

    // Recording tips state
    tipsDismissedForSession: boolean;
    tipsDismissedPermanently: boolean;

    // Simulation state
    simulationCounter: number;

    // Recording stats
    recordingStats?: IRecordingStats;
};
/**
 * iOS reports an interruption as ended while the ending call's own audio session is still being
 * torn down, so reactivating ours throws and the recorder refuses. Waiting and asking again is what
 * gets through: on device, a single attempt resumed roughly every other call.
 */
const RESUME_RETRY_DELAYS_MS = [500, 1500, 3000];

/** Whether the system has said the interruption is over, which is all that licenses giving up. */
interface IResumeTrigger {
    interruptionReportedOver: boolean;
}

export class RecordingState extends State<IRecordingState> {
    static inject = () => [
        RecordingService,
        BufferedEventBus,
        CaremapNotesModelFactory,
        CareMapState,
        AlertPresenter,
        AppPrincipal,
        Logger,
        NetworkAdapter,
        OfflineRecordingQueue,
        MixPanelEventEmitter,
        AsyncStorageProvider,
        RecordingStatsClient,
        UserState,
        DevSettings,
        NotesClient
    ];

    private pendingRecordingCheck?: string;
    private shownProcessingCompleteIds = new Set<string>();
    private shownSuccessModalIds = new Set<string>();
    private shownErrorModalIds = new Set<string>();
    private currentCareMapId?: string;

    // Running time, to compare with the audio the recorder saved. The sheet's timer is a separate
    // instance that resets if its component remounts.
    private recordingStopwatch = new Stopwatch();

    // Two resume attempts can arrive within one bridge round trip, and the loser would otherwise
    // read its own false result as a failure and pause a recording that is running. This covers the
    // triggers below, not the Resume button, which drives the recorder from the component.
    private resumingAfterInterruption = false;

    // A trigger arriving mid-flight is remembered rather than dropped, so a focus gain landing
    // during a foreground attempt still gets its retry. Its claim that the interruption is over
    // travels with it, because that claim is the only thing that licenses giving up.
    private pendingResume?: IResumeTrigger;

    constructor(
        private service: RecordingService,
        private events: BufferedEventBus,
        private notesFactory: CaremapNotesModelFactory,
        private careMapState: CareMapState,
        private alert: AlertPresenter,
        private principal: AppPrincipal,
        private logger: Logger,
        private networkAdapter: NetworkAdapter,
        private offlineQueue: OfflineRecordingQueue,
        private mixpanel: MixPanelEventEmitter,
        private asyncStorage: AsyncStorageProvider,
        private recordingStatsClient: RecordingStatsClient,
        private userState: UserState,
        private devSettings: DevSettings,
        private notesClient: NotesClient
    ) {
        super({
            recordings: [],
            offlineRecordings: [],
            isRecording: false,
            isStartingRecording: false,
            isPaused: false,
            isSheetMinimized: false,
            isOnline: true,
            pendingUploadCount: 0,
            tipsDismissedForSession: false,
            tipsDismissedPermanently: false,
            simulationCounter: 0
        });

        this.load();

        this.events.on(RECORDING_SERVICE_STATUS_EVENT, (payload: IRecording | undefined) => {
            this.onRecordingStatusUpdated(payload);
        });

        // Listen for websocket note updates that might include recording status changes
        this.events.on('web-sockets.message', async (message: any) => {
            if (message.action === 'resourceChanged' && message.resourceToRefresh) {
                // Only check note resources, not sub-resources like reactions
                const isNoteResource = message.resourceToRefresh.match(/\/notes\/[^/]+$/);
                if (isNoteResource) {
                    // Store the resource to check after NotesModel finishes refreshing
                    this.pendingRecordingCheck = message.resourceToRefresh;
                }
            }
        });

        // Listen for NotesModel state changes to detect when notes have been updated
        this.notesFactory.subscribe(() => {
            if (this.pendingRecordingCheck) {
                this.checkForRecordingStatusChange(this.pendingRecordingCheck).catch(err =>
                    this.logger.error(`Error checking recording status change: ${err.toString()} ${JSON.stringify(err)}`)
                );
                this.pendingRecordingCheck = undefined;
            }
        });

        // Listen for UserState changes to load stats when user loads
        this.userState.subscribe(() => {
            if (this.userState.state.userId) {
                this.loadRecordingStats().catch(err =>
                    this.logger.error(`Error loading recording stats on user load: ${err.toString()} ${JSON.stringify(err)}`)
                );
            }
        });

        // Listen for network status changes
        this.networkAdapter.subscribe((state: {connected?: boolean}) => {
            const isOnline = state.connected ?? true;

            this.setState({
                isOnline
            });


            // When coming back online, try to process offline recordings and other recordings that may need uploading
            // Skip if dev setting is enabled (for testing UI with specific statuses)
            if (isOnline && (this.state.offlineRecordings.length > 0 || this.state.recordings.length > 0) && !this.devSettings.disableRecordingProcessing) {
                this.logger.info('Network restored, processing offline recordings', {
                    count: this.state.offlineRecordings.length
                });

                // Process offline recordings safely to avoid duplicates
                this.state.offlineRecordings.forEach(recording => {
                    if (!this.belongsToActiveCareMap(recording)) {
                        return;
                    }
                    this.processRecordingSafely(recording, async () => {
                        try {
                            await this.service.retryUpload(recording.id);
                        } catch (error) {
                            throw error;
                        }
                    });
                });

                // Also process regular recordings that might be in failed or incomplete states
                this.processRecordingsForUploadAndCleanup([...this.state.recordings, ...this.state.offlineRecordings], 'network-restore');

            }

            // Retry any pending direct uploads (recordings that bypassed storage)
            if (isOnline && this.service.hasPendingDirectUploads() && !this.devSettings.disableDirectUploadAutoRetry) {
                this.logger.info('Network restored, retrying pending direct uploads');
                this.service.retryAllPendingDirectUploads();
            }
        });

        // Listen for offline queue changes
        this.offlineQueue.subscribe(() => {
            this.setState({
                pendingUploadCount: this.offlineQueue.getPendingCount()
            });
        });

        // handle a system interruption, such as a call or another app taking the microphone.
        // If this registration ever fails nothing resumes, so it must not fail silently.
        if (isPlatform('capacitor')) {
            VoiceRecorder.addListener('voiceRecordingInterrupted', event => {
                // The sheet stops claiming to record, but the stopwatch keeps running so the
                // seconds the interruption costs are still measured and reported at save.
                if (this.state.isRecording) {
                    // Android knows a permanent loss of focus up front; everywhere else this
                    // starts false and is only set by a resume still refused once the interruption
                    // is over. Read defensively, because a binary older than this bundle sends no
                    // payload. This arrives only for an interruption beginning while the recorder
                    // captures, so it cannot reset one interruption's verdict for the next: see the
                    // give-up branch in resumeWithRetries.
                    this.setState({ isInterrupted: true, needsManualResume: event?.permanent === true });
                }
            }).catch(error => this.logger.error(error));

            VoiceRecorder.addListener('voiceRecordingInterruptionEnded', () => {
                this.resumeAfterInterruption({ interruptionReportedOver: true });
            }).catch(error => this.logger.error(error));

            // Android only reports an interruption as ended when audio focus comes back, which
            // never happens while another app keeps it. iOS does deliver the ended event, but a
            // resume refused on the back of it used to be the end of the recording, so returning to
            // the app is every platform's second chance. Racing the ended event costs nothing:
            // attempts are single-flighted and each one re-reads the recorder's own status.
            //
            // Returning says nothing about the interruption being over, though: a caregiver can
            // open the app mid-call, where every attempt is refused by a call that will hand the
            // microphone back.
            this.events.on(APP_FOREGROUNDED, () => {
                this.resumeAfterInterruption({ interruptionReportedOver: false });
            });
        }

        // Listen for care map changes to process orphaned recordings for the newly active care map
        this.careMapState.subscribe(() => {
            const careMap = this.careMapState.getActiveCaremap();

            // Only process when the care map ID actually changes
            if (careMap && careMap.careMapId !== this.currentCareMapId) {
                this.currentCareMapId = careMap.careMapId;

                // Notify subscribers so UI re-evaluates care-map-dependent filters
                // (e.g. getOfflineRecordingsForActiveCareMap)
                this.setState({});

                // When care map switches, try to process any orphaned recordings that belong to the new care map
                if (this.state.recordings.length > 0 || this.state.offlineRecordings.length > 0) {
                    this.logger.info('Care map changed, processing recordings for active care map');
                    this.processRecordingsForUploadAndCleanup(
                        [...this.state.recordings, ...this.state.offlineRecordings],
                        'network-restore'
                    );
                }
            }
        });

        // even on web we want to process uploads that may be orphaned or pending etc...
        this.events.on(APP_FOREGROUNDED, async () => {
            if (!this.devSettings.disableRecordingProcessing) {
                // Load fresh recordings from IndexedDB and process for upload and cleanup
                // Skip if dev setting is enabled (for testing UI with specific statuses)
                try {
                    const allRecordings = (await this.service.getAll()).filter(r => this.belongsToActiveCareMap(r));
                    this.processRecordingsForUploadAndCleanup(allRecordings, 'load');
                } catch (err: any) {
                    // Tag dev flag simulation errors so Logger can filter them from Sentry
                    if (err?.message?.includes('dev flag enabled') || err?.message?.includes('Simulated:')) {
                        err.errorCode = 'dev-flag-simulation';
                    }
                    this.logger.error(err, { message: 'Error loading recordings on app foreground' });
                    // Storage errors are handled at runtime when user attempts to record
                }

                // Check if any "processing" recordings have completed while app was backgrounded
                this.checkProcessingRecordingsOnForeground().catch(err =>
                    this.logger.error(err, { message: 'Error checking processing recordings on foreground' })
                );
            }
        });
    };

    private hasProcessedLocalRecordings = false;
    private processingRecordings = new Set<string>();
    private processingNotes = new Set<string>();

    private NUM_RECORDINGS_QUESTION = 'num-recordings';
    private TIPS_DISMISSED_PERMANENTLY_KEY = 'recording-tips-dismissed-permanently';

    private tipsDismissalStorage: ReturnType<AsyncStorageProvider['getBooleanProvider']> | undefined;

    private getTipsDismissalStorage() {
        if (!this.tipsDismissalStorage) {
            try {
                this.tipsDismissalStorage = this.asyncStorage.getBooleanProvider(
                    this.TIPS_DISMISSED_PERMANENTLY_KEY,
                    { envNeutral: false, userNeutral: false }
                );
            } catch (err: any) {
                this.logger.error(`Failed to create tips dismissal storage provider: ${err.toString()} ${JSON.stringify(err)}`);
                throw err;
            }
        }
        return this.tipsDismissalStorage;
    }

    private async processRecordingSafely(recording: IRecording, action: () => Promise<void> | void) {
        if (this.processingRecordings.has(recording.id)) {
            return;
        }

        this.processingRecordings.add(recording.id);
        try {
            await action();
        } catch (err: any) {
            // Tag dev flag simulation errors so Logger can filter them from Sentry
            if (err?.message?.includes('dev flag enabled') || err?.message?.includes('Simulated:')) {
                err.errorCode = 'dev-flag-simulation';
            }
            this.logger.error(err, { message: 'Error processing recording', recording });
            throw err;
        } finally {
            this.processingRecordings.delete(recording.id);
        }
    }

    private async processNoteSafely(noteId: string, action: () => Promise<void> | void) {
        if (this.processingNotes.has(noteId)) {
            this.logger.info('Note already being processed, skipping', { noteId });
            return;
        }

        this.processingNotes.add(noteId);
        try {
            await action();
        } finally {
            this.processingNotes.delete(noteId);
        }
    }

    /**
     * Check if a recording belongs to the currently active care map and current user.
     * Returns true for legacy recordings without careMapId for backward compatibility.
     * If userId or careMapUserId are present, they must also match.
     */
    private belongsToActiveCareMap(recording: IRecording): boolean {
        const activeCaremap = this.careMapState.getActiveCaremap();
        const activeCareMapId = activeCaremap?.careMapId;

        // Handle legacy recordings without careMapId
        if (!recording.careMapId) {
            return true;
        }

        if (recording.careMapId !== activeCareMapId) {
            return false;
        }

        // Check care map owner (full composite key)
        if (recording.careMapUserId) {
            if (recording.careMapUserId !== (activeCaremap && 'userId' in activeCaremap ? activeCaremap.userId : undefined)) {
                return false;
            }
        }

        // Check recording creator matches logged-in user
        if (recording.userId) {
            if (recording.userId !== this.principal.userId) {
                return false;
            }
        }

        return true;
    }

    private shouldProcessRecording(recording: IRecording, hasValidNote: boolean): { process: boolean; createNote: boolean } {
        // Skip recordings that are fully uploaded with valid note and URL
        // These don't need reprocessing when switching care maps
        if (recording.status === 'uploaded' && recording.url && recording.noteId && hasValidNote) {
            return { process: false, createNote: false };
        }

        // Statuses that need processing
        const needsUpload = ['uploading', 'upload-failed', 'recording-complete', 'offline-pending'].includes(recording.status);

        if (hasValidNote && needsUpload) {
            // Has a note but needs upload retry
            return { process: true, createNote: false };
        }

        // Handle recordings without valid local note
        if (!hasValidNote && (recording.status === 'offline-pending' || recording.status === 'upload-failed')) {
            if (recording.status === 'offline-pending') {
                this.mixpanel.track('Processing Offline Recording Without Note', () => ({ 'Recording': recording }));
            }

            // Key distinction: does noteId exist?
            if (!recording.noteId) {
                // No noteId - need to create note first
                return { process: true, createNote: true };
            } else {
                // Has noteId but note not in local state - use existing noteId
                return { process: true, createNote: false };
            }
        }

        if (!hasValidNote && recording.status === 'uploading' && !recording.noteId) {
            this.mixpanel.track('Processing Offline Recording Without Note', () => ({ 'Recording': recording }));
            return { process: true, createNote: true };
        }

        return { process: false, createNote: false };
    }

    private processRecordingsForUploadAndCleanup = (recordings: IRecording[], context: 'load' | 'network-restore' = 'load') => {
        // Skip processing if dev setting is enabled (for testing UI with specific statuses)
        if (this.devSettings.disableRecordingProcessing) {
            return;
        }

        const localRecordingsToTrack: IRecording[] = [];

        recordings.forEach(recording => {
            // Only process recordings that belong to the currently active care map
            // This prevents recordings from other care maps from interfering
            if (!this.belongsToActiveCareMap(recording)) {
                return;
            }

            const note = this.findNote(recording.noteId);
            const hasValidNote = !!(note && recording.noteId);

            // 1. Cleanup: Delete recordings that are already uploaded to notes
            if (this.isUploadedToNote(recording) && this.isUploadedStatus(recording.status)) {
                this.service.delete(recording.id);
            }

            // 2. Handle recordings that need processing
            const needsProcessing = this.shouldProcessRecording(recording, hasValidNote);
            if (needsProcessing.process) {
                this.processRecordingSafely(recording, () => {
                    if (needsProcessing.createNote) {
                        this.service.retryUpload(recording.id);
                    } else {
                        this.service.upload(recording);
                    }
                });
            }

            // 3. Handle out-of-sync status updates
            if (recording.status === 'uploaded' && note?.otherAttributes?.recording?.status === 'uploading') {
                this.onRecordingStatusUpdated(recording);
            }

            // Only do logging and tracking during load, not network restore
            if (context === 'load') {
                // if there is an orphaned recording, it is completed but not moved on or upload failed,
                // it might be stuck in indexedDB so let's log it to Sentry
                // doing here because need to make sure notesModel.hasLoaded before checking
                // Only log orphaned recordings that belong to the active care map
                if (!isAutomatedTests() &&
                    dayjs(recording.created).isAfter('2025-07-07') &&
                    this.belongsToActiveCareMap(recording) &&
                    (!this.isUploadedToNote(recording) ||
                    // this can be duplicative with the one above that kicks off the upload
                    recording.status === 'recording-complete' ||
                    recording.status === 'upload-failed' ||
                    recording.status === 'offline-pending')) {
                    this.logger.error('Potentially Orphaned Recording', recording);
                }

                // build failing in github, try again
                // if there are any recordings found locally, collect them for batch tracking
                if (!isAutomatedTests() &&
                    dayjs(recording.created).isAfter('2025-07-07')) {
                    localRecordingsToTrack.push(recording);
                }
            }
        });

        // Send batch events for all local recordings found
        if (context === 'load' && localRecordingsToTrack.length > 0) {
            // Separate recordings by whether they match the active care map
            const matchedRecordings = localRecordingsToTrack.filter(r => this.belongsToActiveCareMap(r));
            const mismatchedRecordings = localRecordingsToTrack.filter(r => !this.belongsToActiveCareMap(r));

            if (matchedRecordings.length > 0) {
                this.mixpanel.track('Local Recordings Found', () => ({
                    'Recordings': matchedRecordings
                }));
            }

            if (mismatchedRecordings.length > 0) {
                this.mixpanel.track('Local Recordings Found - Mismatched Care Profile', () => ({
                    'Recordings': mismatchedRecordings
                }));
            }
        }
    };

    load = async () => {
        let allRecordings: IRecording[] = [];
        try {
            allRecordings = await this.service.getAll();
        } catch (err: any) {
            // Tag dev flag simulation errors so Logger can filter them from Sentry
            if (err?.message?.includes('dev flag enabled') || err?.message?.includes('Simulated:')) {
                err.errorCode = 'dev-flag-simulation';
            }
            this.logger.error(err, { message: 'Error loading recordings on initial load' });
            // Storage errors are handled at runtime when user attempts to record
            return; // Exit early since we can't load recordings
        }

        const onlineRecordings = allRecordings.filter(r => r.status !== 'offline-pending');
        const offlineRecordings = allRecordings.filter(r => r.status === 'offline-pending');

        let isOnline: boolean;
        try {
            isOnline = this.networkAdapter.isOnline;
        } catch (error) {
            // NetworkAdapter not yet initialized, default to true
            isOnline = true;
        }

        this.setState({
            recordings: onlineRecordings,
            offlineRecordings,
            isOnline,
            pendingUploadCount: this.offlineQueue.getPendingCount()
        });

        try {
            this.notesFactory.forActiveCaremap().subscribe(notesModel => {
                // only check on the first load and make sure notes loaded first
                if (!this.hasProcessedLocalRecordings && notesModel.hasLoaded) {
                    // Process both online and offline recordings
                    this.processRecordingsForUploadAndCleanup([...this.state.recordings, ...this.state.offlineRecordings], 'load');

                    // doing here because need to make sure notesModel.hasLoaded before checking
                    this.hasProcessedLocalRecordings = true;
                }
            });
        } catch (error) {
            // TODO: forActiveCaremap throws an error for anonymous
        }
    };

    private handleRecordingInterrupted = async () => {
        this.setState({ recordingInterrupted: true });

        // wait a sec before showing prompt
        await new Promise(res => setTimeout(res, 2000));
        this.setState({ showRecordingInterruptedAlert: true });
    };

    /**
     * The recorder stops capturing when the system interrupts it and never restarts itself, so
     * everything after the interruption is lost unless we resume. Whatever the interruption still
     * costs is measured by the stopwatch and reported when the recording is saved.
     *
     * No unit test reaches this, and extracting the guards into a pure decision function would not
     * change that usefully: each is a line or two whose correctness is its position relative to the
     * awaits, not its boolean. Every change to this function has been about ordering and
     * re-entrancy, which a snapshot taken before an await cannot express. What
     * actually blocks testing is the constructor's fifteen dependencies; reaching it would mean
     * making this class constructible in a test, not wrapping these ifs.
     */
    private resumeAfterInterruption = async ({ interruptionReportedOver }: IResumeTrigger) => {
        // A recording already stopped and saved has nothing to resume.
        if (!this.state.isRecording) {
            return;
        }

        // A caregiver who paused during the interruption must not have capture restart behind a
        // paused sheet. A hold that never reached the recorder leaves it capturing under one, so
        // every trigger re-applies the hold rather than only declining to resume.
        if (this.state.isPaused) {
            await this.holdRecorderWhilePaused();
            return;
        }

        if (this.resumingAfterInterruption) {
            // The stronger claim wins: an ended event landing during a foreground attempt still
            // licenses giving up, which a foreground attempt on its own never does.
            const reportedOver = interruptionReportedOver || this.pendingResume?.interruptionReportedOver;
            this.pendingResume = { interruptionReportedOver: reportedOver === true };

            return;
        }

        this.resumingAfterInterruption = true;
        try {
            const { status } = await VoiceRecorder.getCurrentStatus();

            // The sheet's interrupted state is only ever as true as the plugin's, so every pass
            // heals it: a recording resumed by any other route stops being reported as stopped.
            const stillInterrupted = status === 'INTERRUPTED';

            this.setState({
                isInterrupted: stillInterrupted,
                needsManualResume: stillInterrupted && this.state.needsManualResume
            });
            if (!stillInterrupted) {
                return;
            }

            // A hold placed while that round trip was in flight is the caregiver saying stop, so it
            // wins over anything this pass would otherwise do.
            if (this.state.isPaused) {
                return;
            }

            await this.resumeWithRetries({ interruptionReportedOver });
        } catch (error) {
            this.handleResumeFailure(error);
        } finally {
            this.resumingAfterInterruption = false;

            const pending = this.pendingResume;
            if (pending) {
                this.pendingResume = undefined;
                this.resumeAfterInterruption(pending);
            }
        }
    };

    /**
     * Asks the recorder again after a refusal, because a refusal is usually only about timing: see
     * RESUME_RETRY_DELAYS_MS. The guards sit at the top of the loop rather than in a helper so that
     * each one is read after the wait and before the next attempt, since a caregiver can save,
     * discard or hold the recording while it waits.
     */
    private resumeWithRetries = async ({ interruptionReportedOver }: IResumeTrigger) => {
        for (let attempt = 0; attempt <= RESUME_RETRY_DELAYS_MS.length; attempt++) {
            // Waking a recorder that is stopping leaves iOS with a stray segment and an audio
            // session it never restores.
            if (!this.state.isRecording) {
                return;
            }

            // A hold placed during a wait is the caregiver saying stop, and it has to reach a
            // recorder whose status reading is by now up to three seconds stale.
            if (this.state.isPaused) {
                await this.holdRecorderWhilePaused();

                return;
            }

            const { value: resumed } = await VoiceRecorder.resumeRecording();
            if (resumed) {
                this.setState({ isInterrupted: false, needsManualResume: false });
                await this.holdRecorderWhilePaused();

                return;
            }

            const { status } = await VoiceRecorder.getCurrentStatus();
            if (status !== 'INTERRUPTED') {
                // Whatever it is now, the phone is no longer holding it, so the sheet catches up.
                this.setState({ isInterrupted: false, needsManualResume: false });

                return;
            }

            if (attempt < RESUME_RETRY_DELAYS_MS.length) {
                await this.wait(RESUME_RETRY_DELAYS_MS[attempt]);
            }
        }

        // A refusal only says nothing will pick this up once the system has said the interruption
        // is over, and claiming otherwise would take away Pause, the only way to refuse the restart.
        //
        // The claim comes from the trigger and is not re-checked, so a second interruption starting
        // inside the retry window is read as the first one's failure. The plugin raises no begin
        // event for it once the recorder is interrupted, and whether iOS posts one is untested, so
        // nothing here can tell the two apart. Bounded: no audio is captured meanwhile, Save stays
        // on screen, and the second interruption's own ended event resumes the recording.
        //
        // The patch could forward that event and a counter here could then skip this write. Not
        // done: on Android it would mean forwarding every transient loss, ducking included, which
        // would reset the verdict repeatedly and could hold a recording in the hedged copy
        // indefinitely, trading a bounded window for an unbounded one.
        if (interruptionReportedOver) {
            this.setState({ needsManualResume: true });
        }

        this.logger.info('Could not resume an interrupted recording', { interruptionReportedOver });
    };

    private wait = (ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms));

    /**
     * Stops the recorder capturing whenever the sheet says the caregiver has paused it. A refusal
     * means it was not capturing, which is the state a hold wants anyway.
     */
    private holdRecorderWhilePaused = async () => {
        if (!this.state.isPaused) {
            return;
        }

        try {
            await VoiceRecorder.pauseRecording();
        } catch (error) {
            // A save or a discard landing mid-call tears the recorder down, and a hold on a
            // recording that no longer exists is nothing to report.
            if (!this.state.isRecording) {
                const detail = error instanceof Error ? error.message : String(error);
                this.logger.info('A paused recording stopped before it could be held', { error: detail });
                return;
            }

            // Capture carries on under a paused sheet until the next trigger re-applies the hold or
            // the caregiver taps Resume. Failing out of the caller instead would end the recording
            // and call a failed hold an interruption.
            this.logger.error(error, { message: 'Could not hold a recording the caregiver paused' });
        }
    };

    private handleResumeFailure = (error: unknown) => {
        // A save or a discard landing mid-pass makes the plugin throw, and there is then
        // nothing left to save or to tell anyone about, which is why dropping it is safe.
        // Logger.info is console-only, so this reaches an attached inspector and nowhere else.
        if (!this.state.isRecording) {
            // The message, not the error: scrubSensitiveKeys flattens an Error passed as extras.
            const detail = error instanceof Error ? error.message : String(error);
            this.logger.info('An interrupted recording stopped before it could be resumed', { error: detail });
            return;
        }

        this.logger.error(error);

        // Nothing more can be captured, so save what exists and tell the caregiver.
        this.handleRecordingInterrupted();
    };

    start = () => {
        this.recordingStopwatch.start();
        this.setState({ missingAudioMs: undefined, isInterrupted: false, needsManualResume: false });

        // Don't set a default recording context - allow it to be undefined for menu bar recordings
        // Context buttons explicitly set their context before recording starts

        // Start recording and reset session tips dismissal for new recording session.
        // Clearing isStartingRecording in the same update hands the screen straight from the
        // busy control to the recording sheet, with no frame showing neither of them.
        this.setState({
            isRecording: true,
            isStartingRecording: false,
            tipsDismissedForSession: false
        });

        // Force reload of permanent dismissal preference
        this.tipsDismissalLoaded = false;
        this.loadTipsDismissalPreference();
    };

    stop = () => {
        // Frozen here so the time taken to save does not count as recorded time.
        this.recordingStopwatch.pause();

        // Note: currentRecordingExtras is intentionally NOT cleared here.
        // add() reads extras (summaryPrompt, appointmentId) after stop() is called,
        // then clears them itself. Clearing here would wipe data before it's consumed.
        this.setState({
            isPaused: false,
            isRecording: false,
            isStartingRecording: false,
            forceStartRecording: false,
            isInterrupted: false,
            needsManualResume: false
        });
    };

    setStartingRecording = (isStartingRecording: boolean) => {
        this.setState({ isStartingRecording });
    };

    pause = () => {
        this.recordingStopwatch.pause();
        this.setState({ isPaused: true });
    };

    resume = () => {
        this.recordingStopwatch.resume();
        this.setState({ isPaused: false });
    };

    minimizeSheet = () => {
        this.setState({ isSheetMinimized: true });
    };

    maximizeSheet = () => {
        this.setState({ isSheetMinimized: false });
    };

    toggleSheet = () => {
        this.setState({ isSheetMinimized: !this.state.isSheetMinimized });
    };

    isPaused = () => this.state.isPaused;

    isInterrupted = () => this.state.isInterrupted;

    needsManualResume = () => this.state.needsManualResume;

    isRecording = () => this.state.isRecording;

    isStartingRecording = () => this.state.isStartingRecording;

    isSheetMinimized = () => this.state.isSheetMinimized;

    add = async (recording: IRecordingData, autoSaved?: boolean) => {
        const wasInterrupted = this.state.recordingInterrupted || false;

        // An upload never ran the stopwatch, so there is no running time to compare its audio with.
        const missingAudioMs = recording.isUpload ? 0 : getMissingAudioMs({
            elapsedMs: this.recordingStopwatch.elapsedMs(),
            msDuration: recording.msDuration
        });

        // Read by the interrupted alert, which fires without the saved-success event the modal uses.
        this.setState({ missingAudioMs });

        if (missingAudioMs) {
            this.mixpanel.track('Recording Audio Missing', () => ({
                'Missing Seconds': Math.round(missingAudioMs / 1000),
                'Duration Seconds': Math.round(recording.msDuration / 1000)
            }));
        }

        const newRecording = await this.service.add(
            recording,
            {
                autoSaved,
                skipAIProcessing: this.shouldSkipAIProcessing(),
                appointmentId: this.state.currentRecordingExtras?.appointmentId,
                summaryPrompt: this.state.currentRecordingExtras?.summaryPrompt,
                // upload passes noteTarget on the recording data; voice sets it via triggerRecording extras
                noteTarget: recording.noteTarget ?? this.state.currentRecordingExtras?.noteTarget,
                isUpload: recording.isUpload,
                wasInterrupted,
                missingAudioMs
            }
        );

        // Add to appropriate list based on status
        if (newRecording.status === 'offline-pending') {
            this.setState({
                offlineRecordings: [...this.state.offlineRecordings, newRecording],
                currentRecordingExtras: undefined
            });
        } else {
            this.setState({
                recordings: [...this.state.recordings, newRecording],
                currentRecordingExtras: undefined
            });
        }
    };

    /**
     * True when nothing has been captured yet for the active care map: no notes carrying a
     * recording and nothing queued offline. Naming a care recipient does not make this false;
     * whether the next recording gets the intro treatment is a separate question (see
     * shouldUseIntroFlow).
     */
    hasNoRecordingsYet = (): boolean => {
        try {
            const notes = this.notesFactory.forActiveCaremap().getNotes();
            const hasNotesWithRecordings = notes.some(n => n.otherAttributes?.recording);
            // Scoped to the active care map, like the notes above: offlineRecordings holds
            // pending recordings for every care map, and a sibling's must not count as this
            // one having captured something.
            const hasOfflineRecordings = this.getOfflineRecordingsForActiveCareMap().length > 0;
            return !hasNotesWithRecordings && !hasOfflineRecordings;
        } catch {
            // No active caremap — nothing has been recorded
            return true;
        }
    };

    /**
     * Returns true if the next recording should be treated as an intro recording: one that
     * runs the profile-extraction prompt and skips the consent modal. True when nothing has
     * been recorded yet AND there is no care recipient to extract into.
     */
    shouldUseIntroFlow = (): boolean => {
        const active = this.careMapState.getActiveCaremap();
        // Once an editor has created a care recipient we're past intro flow even with
        // zero notes: there is already a profile to extract into. Gated to editors because a
        // view-only care map can be the active one, and someone else's completed profile must
        // not count as our own past-intro state. A blank caremap from the voice-first add flow
        // (patient with no name) stays incomplete, so it is still treated as intro.
        const canEdit = Boolean(active?.links?.self?.actions?.includes('put'));
        if (active && canEdit && !this.careMapState.isIncomplete(active)) {
            return false;
        }
        return this.hasNoRecordingsYet();
    };

    // assumes NotesModel.hasLoaded = true
    findNote = (noteId: string) => {
        if (!this.careMapState.hasActiveCareMap()) {
            return undefined;
        }
        const notes = this.notesFactory.forActiveCaremap().getNotes();
        return notes.find(n => n.noteId === noteId);
    };

    // assumes NotesModel.hasLoaded = true
    isUploadedToNote = (recording: IRecording) => {
        const recordingNote = this.findNote(recording.noteId);
        return recordingNote?.attachments.find(a => a.url === recording.url) ? true : false;
    };

    isUploadedStatus = (status: IRecordingStatus | undefined) => {
        return status && status !== 'recording-complete' && status !== 'uploading' && status !== 'upload-failed' && status !== 'offline-pending';
    };

    onRecordingStatusUpdated = async (payload: IRecording | undefined) => {
        if (payload) {
            // Only process recordings that belong to the currently active care map
            // because we need to be able to find and update the note
            if (!this.belongsToActiveCareMap(payload)) {
                return;
            }

            // Use note-based safe processing to prevent race conditions with other note updates
            await this.processNoteSafely(payload.noteId, async () => {
                try {
                    const noteVM = await this.notesFactory.createEditForm(payload.noteId);
                    let note = noteVM.getNote();

                    // update otherAttributes.recording
                    note = {
                        ...note,
                        otherAttributes: {
                            ...note.otherAttributes,
                            recording: this.service.stripRecordingDataForNote(payload)
                        }
                    };

                    if (payload.status === 'uploaded') {
                        // add the attachment
                        note = {
                            ...note,
                            attachments: [
                                ...note.attachments,
                                {
                                    contentType: payload.mimeType,
                                    fileName: payload.fileName,
                                    url: payload.url
                                }
                            ]
                        };
                    }

                    noteVM.setNote(note);

                    // skip certain Mixpanel tracking
                    await noteVM.save({ skipTracking: true });

                    // Emit processing complete event when recording is summarized or processing is complete without summary
                    if (payload.status === 'summarized' || payload.status === 'recording-complete') {
                        await this.emitProcessingComplete(payload);
                    }

                    if (payload.status === 'uploaded') {
                        // Update num-recordings wizard answer now that recording is uploaded and attached to note
                        let numRecordings = this.getNumCareMapRecordings();
                        this.careMapState.answerInitialWizardQuestion({
                            questionId: this.NUM_RECORDINGS_QUESTION,
                            answer: ++numRecordings
                        });

                        // For uploaded recordings, we've just added the attachment and saved the note
                        // so we can safely delete from IndexedDB. The timing issue with isUploadedToNote
                        // can cause uploaded recordings to not be cleaned up properly.
                        this.service.delete(payload.id);

                        // Update the recording status in state first, then remove after a brief delay
                        // This ensures the UI shows the correct 'uploaded' status before cleanup
                        const wasOffline = this.state.offlineRecordings.find((r: IRecording) => r.id === payload.id);
                        if (wasOffline) {
                            this.setState({
                                offlineRecordings: replaceItem(this.state.offlineRecordings, payload, 'id')
                            });
                        } else {
                            this.setState({
                                recordings: replaceItem(this.state.recordings, payload, 'id')
                            });
                        }

                        // Remove from state after a short delay to allow UI to update
                        setTimeout(() => {
                            this.setState({
                                recordings: this.state.recordings.filter((r: IRecording) => r.id !== payload.id),
                                offlineRecordings: this.state.offlineRecordings.filter((r: IRecording) => r.id !== payload.id)
                            });
                        }, 500); // 500ms delay - enough for UI to show uploaded status
                    } else {
                        // Update in appropriate list based on status
                        if (payload.status === 'offline-pending') {
                            this.setState({
                                offlineRecordings: replaceItem(this.state.offlineRecordings, payload, 'id')
                            });
                        } else {
                            // Move from offline to online list if status changed
                            const wasOffline = this.state.offlineRecordings.find((r: IRecording) => r.id === payload.id);
                            if (wasOffline) {
                                this.setState({
                                    recordings: [...this.state.recordings, payload],
                                    offlineRecordings: this.state.offlineRecordings.filter((r: IRecording) => r.id !== payload.id)
                                });
                            } else {
                                this.setState({
                                    recordings: replaceItem(this.state.recordings, payload, 'id')
                                });
                            }
                        }
                    }
                } catch (error: any) {
                    console.log(error);

                    // Don't log to Sentry during automated tests
                    if (!isAutomatedTests()) {
                        // Tag dev flag simulation errors so Logger can filter them from Sentry
                        if (error?.message?.includes('dev flag enabled') || error?.message?.includes('Simulated:')) {
                            error.errorCode = 'dev-flag-simulation';
                        }
                        this.logger.error(error);
                    }
                }
            });
        }
    };

    getOfflineRecordingsForActiveCareMap(): IRecording[] {
        return (this.state.offlineRecordings || []).filter(r => this.belongsToActiveCareMap(r));
    }

    private getCareMap = () => {
        return this.careMapState.getActiveCaremap();
    };

    private getNumCareMapRecordings = (): number => {
        return this.getCareMap()?.initialWizardAnswers[this.NUM_RECORDINGS_QUESTION] || 0;
    };

    private isRecordingLimitReached = (limits: IFreeTierLimits) => {
        return this.getNumCareMapRecordings() >= limits.freeRecordings;
    };

    // Only automated tests set this from here (the dev Test Recording Tools can too). No user path
    // does: the free tier caps how many recordings can
    // be made at all, and the recorder is unavailable once that cap is reached, so nobody can produce
    // a recording that is never sent for processing. (A recording can still end up without a summary
    // for other reasons -- no speech detected in it, or processing failed -- which this flag says
    // nothing about.)
    private shouldSkipAIProcessing = () => {
        return isAutomatedTests() && localStorage.getItem('ENABLE_AI_PROCESSING') !== 'true';
    };

    requiresInnerhivePlus = (limits: IFreeTierLimits) => {
        return this.isRecordingLimitReached(limits);
    };

    // The recording cap is the only thing that opens the paywall from the recorder, and the caller
    // has already established it is reached, so there is nothing to choose between here.
    getInnerhivePlusTriggerSource = (): ITriggerPaywallSource => {
        return 'recordings';
    };

    findNoteForAppointment = (apptId: string): ICaremapNoteResponse | undefined => {
        const notes = this.notesFactory.forActiveCaremap().getNotes();
        return notes.find(note => note?.otherAttributes?.recording?.appointmentId === apptId);
    };

    updateCurrentRecordingExtras = (extras: Partial<ICurrentRecordingExtras>) => {
        this.setState({
            currentRecordingExtras: {...this.state.currentRecordingExtras, ...extras}
        });
    };

    // Triggers recording via a useEffect in Recorder that watches forceStartRecording.
    // This indirection is needed because recording startup logic (permissions, consent,
    // VoiceRecorder) lives in the Recorder component, not in this state class.
    triggerRecording = (extras?: Partial<ICurrentRecordingExtras>) => {
        if (extras) {
            this.updateCurrentRecordingExtras(extras);
        }
        this.setState({ forceStartRecording: true });
    };

    toggleActionItemCompletion = async (noteId: string, actionItemId: string) => {
        try {
            const noteVM = await this.notesFactory.createEditForm(noteId);
            let note = noteVM.getNote();

            if (note.otherAttributes?.recording?.summary?.actionItems) {
                const actionItems = note.otherAttributes.recording.summary.actionItems.map(item => {
                    if (item.id === actionItemId) {
                        const isCompleted = !item.completed;
                        return {
                            ...item,
                            completed: isCompleted,
                            completedAt: isCompleted ? new Date().toISOString() as ISO8601 : undefined,
                            completedBy: isCompleted ? this.principal.userId : undefined
                        };
                    }
                    return item;
                });

                note = {
                    ...note,
                    otherAttributes: {
                        ...note.otherAttributes,
                        recording: {
                            ...note.otherAttributes.recording,
                            summary: {
                                ...note.otherAttributes.recording.summary,
                                actionItems
                            }
                        }
                    }
                };

                noteVM.setNote(note);
                await noteVM.save({ skipTracking: true });
            }
        } catch (error: any) {
            error.displayMessage = 'Unable to toggle action item, please try again.';
            this.alert.showAndLogError(error);
        }
    };

    saveSummaryRating = async (noteId: string, summaryRating: 'positive' | 'negative') => {
        try {
            const note = this.findNote(noteId);
            if (note?.links?.self) {
                await this.notesClient.putSummaryRating(note, summaryRating);
            }
        } catch (error: any) {
            error.displayMessage = 'Unable to save rating, please try again.';
            this.alert.showAndLogError(error);
        }
    };

    getActionItemCompletionStats = (recording: IRecording) => {
        const actionItems = recording.summary?.actionItems || [];
        const completed = actionItems.filter(item => item.completed).length;
        const total = actionItems.length;
        return { completed, total, percentage: total > 0 ? Math.round((completed / total) * 100) : 0 };
    };

    getActionItemsByCategory = (recording: IRecording) => {
        const actionItems = recording.summary?.actionItems || [];

        const categorized = {
            'take-action-today': actionItems.filter(item => item.category === 'take-action-today'),
            'schedule-these': actionItems.filter(item => item.category === 'schedule-these'),
            'ongoing-care': actionItems.filter(item => item.category === 'ongoing-care'),
            'uncategorized': actionItems.filter(item => !item.category)
        };

        return categorized;
    };

    getCategoryDisplayName = (category: string) => {
        const categoryMap: Record<string, string> = {
            'take-action-today': 'Take Action Today',
            'schedule-these': 'Schedule These',
            'ongoing-care': 'Ongoing Care',
            'uncategorized': 'Other Actions'
        };
        return categoryMap[category] || category;
    };

    getCategoryCompletionStats = (recording: IRecording, category: string) => {
        const categorizedItems = this.getActionItemsByCategory(recording);
        const items = (categorizedItems as any)[category] || [];
        const completed = items.filter((item: any) => item.completed).length;
        const total = items.length;
        return { completed, total, percentage: total > 0 ? Math.round((completed / total) * 100) : 0 };
    };

    // Offline recording management
    getOfflineRecordings = (): IRecording[] => {
        return this.state.offlineRecordings;
    };

    getAllRecordings = (): IRecording[] => {
        return [...this.state.recordings, ...this.state.offlineRecordings];
    };

    isActivelyRecordingIntro = (): boolean => {
        return this.state.isRecording && this.state.currentRecordingExtras?.summaryPrompt === 'intro';
    };

    // Statuses a recording never leaves: it is summarized, or it failed at one of the stages
    // on the way there. Anything else is still moving.
    private settledStatuses: IRecordingStatus[] = ['summarized', 'summarize-failed', 'transcribe-failed', 'upload-failed'];

    /**
     * True while a summary may still arrive for this INTRO recording. Every "we're working on it"
     * affordance must be driven by this, or it waits on something that never comes: a recording
     * with skipAIProcessing is never sent for AI processing at all and settles at 'uploaded',
     * and a recording that failed to upload or transcribe never reaches 'summarized' either.
     *
     * Intro-only because the server treats 'recording-complete' as a terminal "no speech detected"
     * result for other recordings while always carrying intros through to summarization, so for a
     * non-intro caller this would report a finished recording as still working.
     */
    isAwaitingAIProcessing = (recording: { status?: IRecordingStatus; skipAIProcessing?: boolean } | undefined): boolean => {
        if (!recording || recording.skipAIProcessing) {
            return false;
        }
        return !recording.status || !this.settledStatuses.includes(recording.status);
    };

    // Three windows to cover:
    //   1. currentRecordingExtras — set from triggerRecording() until add() commits.
    //   2. state.recordings — populated by add(), but cleaned up ~500ms after status
    //      hits 'uploaded' (see line 841-846), so it only covers the upload window.
    //   3. note.otherAttributes.recording — durable across the upload→summarized
    //      backend-processing window after state.recordings is cleaned up.
    // The 5-min cutoff is a safety hatch — if processing gets stuck, the UI won't be
    // locked indefinitely. No timer is scheduled at the boundary; natural state
    // changes (websocket updates, foreground rechecks, user interaction) retrigger
    // subscribers within that window.
    isProcessingIntroRecording = (): boolean => {
        if (this.state.currentRecordingExtras?.summaryPrompt === 'intro') {
            return true;
        }

        const cutoffMinutes = 5;
        const isInflightIntro = (r?: Partial<IRecording>): boolean => {
            if (!r || r.summaryPrompt !== 'intro' || !r.created) {
                return false;
            }
            // Settled — button should re-enable so user can retry / continue.
            if (!this.isAwaitingAIProcessing(r)) {
                return false;
            }
            return dayjs().diff(dayjs(r.created), 'minute') < cutoffMinutes;
        };

        if (this.getAllRecordings().some(isInflightIntro)) {
            return true;
        }

        try {
            const notes = this.notesFactory.forActiveCaremap().getNotes();
            return notes.some(n => isInflightIntro(n.otherAttributes?.recording));
        } catch {
            // forActiveCaremap throws for anonymous users / pre-load — treat as idle.
            return false;
        }
    };

    isOnline = (): boolean => {
        return this.state.isOnline;
    };

    getPendingUploadCount = (): number => {
        return this.state.pendingUploadCount;
    };

    retryOfflineRecording = async (recordingId: string) => {

        await this.service.retryUpload(recordingId);
    };

    hasOfflineRecordings = (): boolean => {
        return this.state.offlineRecordings.length > 0;
    };

    getRecordingStatus = (recordingId: string): IRecordingStatus | undefined => {
        const recording = this.getAllRecordings().find(r => r.id === recordingId);
        return recording?.status;
    };

    // Force sync all offline recordings
    syncOfflineRecordings = async () => {
        if (!this.networkAdapter.isOnline) {
            return;
        }

        for (const recording of this.state.offlineRecordings) {
            await this.service.retryUpload(recording.id);
        }
    };


    // Get current recording context
    getCurrentRecordingContext = (): 'at-appointment' | 'post-appointment' | undefined => {
        return this.state.currentRecordingExtras?.recordingContext;
    };

    // Recording tips management
    private tipsDismissalLoaded = false;

    private loadTipsDismissalPreference = async () => {
        if (this.tipsDismissalLoaded) {
            return;
        }

        try {
            const dismissed = await this.getTipsDismissalStorage().get();
            this.setState({ tipsDismissedPermanently: dismissed === true });
            this.tipsDismissalLoaded = true;
        } catch (err: any) {
            this.logger.error(`Failed to load tips dismissal preference: ${err.toString()} ${JSON.stringify(err)}`);
            // Don't mark as loaded if the storage provider isn't ready yet
            // This allows retry when the provider becomes available
            const errorMessage = err instanceof Error ? err.message : String(err);
            const isStorageNotReady = errorMessage.includes('userId is not yet known') ||
                                     errorMessage.includes('env is not yet known');
            if (!isStorageNotReady) {
                this.tipsDismissalLoaded = true; // Mark as loaded for other errors to prevent infinite retries
            }
        }
    };

    shouldShowTips = (): boolean => {
        // Trigger async loading if not already loaded
        if (!this.tipsDismissalLoaded) {
            this.loadTipsDismissalPreference();
        }

        return !this.state.tipsDismissedForSession && !this.state.tipsDismissedPermanently;
    };

    dismissTipsForSession = () => {
        this.setState({ tipsDismissedForSession: true });
    };

    dismissTipsPermanently = async () => {
        try {
            await this.getTipsDismissalStorage().set(true);
            this.setState({ tipsDismissedPermanently: true });
        } catch (err: any) {
            this.logger.error(`Failed to save tips dismissal preference: ${err.toString()} ${JSON.stringify(err)}`);
        }
    };

    removePermanentTipsDismissal = async () => {
        try {
            await this.getTipsDismissalStorage().remove();
            this.setState({ tipsDismissedPermanently: false });
        } catch (err: any) {
            this.logger.error(`Failed to remove tips dismissal preference: ${err.toString()} ${JSON.stringify(err)}`);
        }
    };

    // Save dismissal preference to storage without affecting current session
    saveTipsDismissalPreference = async (dismissed: boolean) => {
        try {
            if (dismissed) {
                await this.getTipsDismissalStorage().set(true);
            } else {
                await this.getTipsDismissalStorage().remove();
            }
        } catch (err: any) {
            this.logger.error(`Failed to save tips dismissal preference: ${err.toString()} ${JSON.stringify(err)}`);
        }
    };

    hasShownProcessingComplete = (recordingId: string): boolean => {
        return this.shownProcessingCompleteIds.has(recordingId);
    };

    markProcessingCompleteShown = (recordingId: string): void => {
        this.shownProcessingCompleteIds.add(recordingId);
    };

    hasShownSuccessModal = (recordingId: string): boolean => {
        return this.shownSuccessModalIds.has(recordingId);
    };

    markSuccessModalShown = (recordingId: string): void => {
        this.shownSuccessModalIds.add(recordingId);
    };

    hasShownErrorModal = (recordingId: string): boolean => {
        return this.shownErrorModalIds.has(recordingId);
    };

    markErrorModalShown = (recordingId: string): void => {
        this.shownErrorModalIds.add(recordingId);
    };

    checkStorageConnection = async (): Promise<boolean> => {
        try {
            await this.service.checkStorageConnection();
            return true;
        } catch (error) {
            // Storage unavailable - check if we can proceed with direct upload
            if (this.networkAdapter.isOnline) {
                // Online: allow recording, direct upload will handle it
                return true;
            }

            // Offline + storage failed = unrecoverable, block recording
            this.showStorageUnavailableError();
            return false;
        }
    };

    private showStorageUnavailableError = () => {
        const isWeb = !isPlatform('capacitor');
        const message = isWeb
            ? 'Recording requires either an internet connection or working browser storage. Try closing this tab and reopening the app, or connect to the internet.'
            : 'Recording requires either an internet connection or working device storage. Try closing and restarting the app, or connect to the internet.';

        this.events.emit(RECORDING_ERROR_EVENT, {
            recordingId: 'storage-unavailable',
            errorType: 'storage-and-network-failed',
            title: 'Quick Fix Needed',
            message
        });
    };

    private getShortRecordingMessage(): string {
        return RecordingMessages.getShortRecordingMessage();
    }

    private emitProcessingComplete = async (recording: IRecording) => {
        try {
            // Classify the recording for short recording detection
            const classification = classifyRecording(recording);

            // Fetch real stats from API
            const stats = await this.recordingStatsClient.getRecordingStats();
            const message = getSuccessMessage(stats);

            // Update cached stats
            this.setState({ recordingStats: stats });

            // Always emit PROCESSING_COMPLETE with classification data
            // The event handler will decide which modal to show
            // Only include shortRecordingMessage if dev flag allows it
            this.events.emit('PROCESSING_COMPLETE', {
                message,
                stats,
                recordingId: recording.id,
                noteId: recording.noteId,
                classification,
                msDuration: recording.msDuration,
                summaryPrompt: recording.summaryPrompt,
                extractedFields: recording.extractedFields,
                shortRecordingMessage: (classification.isShort && !classification.hasSummary && !this.devSettings.ignoreShortRecordingMessages)
                    ? this.getShortRecordingMessage()
                    : undefined
            });
        } catch (err: any) {
            this.logger.error(`Failed to fetch recording stats: ${err.toString()} ${JSON.stringify(err)}`);
            // Don't emit the event if we can't get stats
        }
    };

    // Message Selection Algorithm
    getSuccessMessage(isOffline?: boolean): { title: string; message: string } {
        return RecordingMessages.getSuccessMessage(isOffline);
    }

    /**
     * Everything the modal shown after a recording is captured needs. Chosen here rather than in
     * the component so "what does the user get told happens next" has one answer, and so the
     * component needs no knowledge of which recordings get summarized.
     */
    getSavedModalContent = ({ isOffline, summaryPrompt, skipAIProcessing, firstName, missingAudioMs }: {
        isOffline?: boolean;
        summaryPrompt?: ISummaryPrompt;
        skipAIProcessing?: boolean;
        firstName: string;
        missingAudioMs?: number;
    }): IRecordingSavedModalContent => {
        // Said first because it is the only message here the caregiver may need to act on, but it
        // keeps the intro flow's own shape so onboarding still behaves as it does elsewhere.
        if (missingAudioMs) {
            return {
                ...RecordingMessages.getMissingAudioMessage({ missingAudioMs, isOffline }),
                isIntro: summaryPrompt === 'intro',
                showProcessingIndicator: !skipAIProcessing,
                buttonText: summaryPrompt === 'intro' ? 'Sounds Good' : 'Got It!'
            };
        }

        if (skipAIProcessing) {
            return {
                ...RecordingMessages.getNoSummaryMessage({ isOffline }),
                isIntro: false,
                showProcessingIndicator: false,
                buttonText: 'Got It!'
            };
        }

        if (summaryPrompt === 'intro') {
            return {
                ...RecordingMessages.getIntroSuccessMessage(firstName),
                isIntro: true,
                showProcessingIndicator: true,
                buttonText: 'Sounds Good'
            };
        }

        return {
            ...RecordingMessages.getSuccessMessage(isOffline),
            isIntro: false,
            showProcessingIndicator: true,
            buttonText: 'Got It!'
        };
    };

    // Check if a note update includes a recording status change to 'summarized'
    private checkForRecordingStatusChange = async (noteResource: string) => {
        try {
            // Get the updated note from NotesModel (which has already been refreshed)
            const notesModel = this.notesFactory.forActiveCaremap();
            const note = notesModel.getNotes().find(n => n.links.self.href === noteResource);

            if (!note) {
                return;
            }

            // Recording-progress UI (processing-complete / summary-ready) is for the person who made
            // the recording, not co-owners. Since IH-6930 a co-owner's recording note appears live in
            // this list too, but it must arrive silently for them — so don't announce a recording this
            // user didn't author. (The recorder is the note's createdBy.)
            if (note.createdBy !== this.principal.userId) {
                return;
            }

            if (note.otherAttributes?.recording) {
                const recording = note.otherAttributes.recording as IRecording;

                // Check if this recording just became 'summarized' or 'recording-complete' (e.g. no speech detected)
                if (recording.status === 'summarized' || recording.status === 'recording-complete') {
                    // Check if we already know about this recording having this status
                    const existingRecording = this.getAllRecordings().find(r => r.id === recording.id);

                    // Only emit if this is a new status (not already at this status)
                    if (!existingRecording || existingRecording.status !== recording.status) {
                        await this.emitProcessingComplete(recording);
                    }
                }
            }
        } catch (error) {
            // Ignore errors - note might not exist or not be accessible
            // This is expected for non-note resources or notes without recordings
        }
    };

    // On app foreground, check if any recordings that were being processed on the backend
    // have completed while the app was backgrounded (and we missed the websocket message).
    private checkProcessingRecordingsOnForeground = async () => {
        if (!this.careMapState.hasActiveCareMap()) {
            return;
        }

        const notesModel = this.notesFactory.forActiveCaremap();
        if (!notesModel.hasLoaded) {
            return;
        }

        // Find notes with recordings in a "backend is processing" state
        const processingNotes = notesModel.getNotes().filter(note => {
            const status = (note.otherAttributes?.recording as IRecording | undefined)?.status;
            return status === 'uploaded' || status === 'transcribed';
        });

        for (const note of processingNotes) {
            const noteResource = note.links.self.href;
            try {
                await notesModel.refresh(noteResource);
                await this.checkForRecordingStatusChange(noteResource);
            } catch (err: any) {
                this.logger.error(err, { message: 'Error checking processing recording on foreground' });
            }
        }
    };

    loadRecordingStats = async () => {
        try {
            const stats = await this.recordingStatsClient.getRecordingStats();
            this.setState({ recordingStats: stats });
        } catch (err: any) {
            // Don't log this error when automated tests are running
            if (!isAutomatedTests()) {
                this.logger.error(`Failed to load recording stats: ${err.toString()} ${JSON.stringify(err)}`);
            }
        }
    };

    simulateRecordingSuccess = () => {
        const currentCounter = this.state.simulationCounter;
        const newCounter = currentCounter + 1;
        this.setState({ simulationCounter: newCounter });

        // getFakeRecordingStats already adds 1, so pass current counter
        const stats = getFakeRecordingStats(currentCounter);
        const message = getSuccessMessage(stats);

        this.events.emit('PROCESSING_COMPLETE', {
            message,
            stats,
            recordingId: `simulated-${newCounter}`,
            noteId: `simulated-note-${newCounter}`
        });
    };

};
